<template>
	<!-- EXP MERGE BUY RENT -->
	<div v-if="expMergeBuyRentVariant && expMergeBuyRentVariant !== 'control'">
		<ListPromotionRowMBRExp
			:title="title"
			:offers="offers"
			:additionalContexts="additionalContexts"
			:hardcodedFallbackOfferId="hardcodedFallbackOfferId"
			:hardcodedCountry="hardcodedCountry"
			:hardcodedShortName="hardcodedShortName"
			:hideRemoveAds="hideRemoveAds"
			:swiperIndex="swiperIndex"
			:largeFreeTrial="largeFreeTrial"
			@clickOut="$emit('clickOut', $event)"
			@impressionCallback="$emit('impressionCallback')"
		/>
	</div>
	<!-- /EXP MERGE BUY RENT -->

	<div v-else-if="promotionOffer && clickoutLink" ref="container" class="offer-wrapper" data-allow-mismatch="class">
		<component
			:is="isModalProvider ? 'div' : ClickoutAnchor"
			:href="clickoutLink"
			:skipPreAffiliationCheck="!inPromotionCatalogue"
			:rel="isModalProvider ? undefined : 'nofollow noopener'"
			:class="[
				'offer',
				{
					'has-border': !activeVariant && isHighlighted,
					'is-large-freetrial': largeFreeTrial,
					'p-0': !!activeVariant,
				},
			]"
			clickoutType="free_trial"
			@click="isModalProvider ? openModal() : onClickOut()"
			v-observe-visibility="observeVisibilityOptions"
		>
			<PromotionRowUISoftExp
				v-if="activeVariant"
				:title="title"
				key="promotion-row-ui-soft"
				:promotionOffer="promotionOffer"
				:packagePlan="packagePlan"
				:inPromotionCatalogue="inPromotionCatalogue"
				:catalogueItem="catalogueItem"
				:filterOption="filterOption"
				:hardcodedCountry="hardcodedCountry"
				:largeFreeTrial="largeFreeTrial"
				:hasPromotedTag="hasPromotedTag"
				:freeTrialDetails="freeTrialDetails"
			/>
			<div class="offer__content grid-container" :class="{ 'grid-container-three-rows': largeFreeTrial }" v-else>
				<div class="picture-wrapper grid-item-a">
					<ProviderIcon
						:key="`list-provider-icon-${promotionOffer.package.shortName}`"
						:iconUrl="promotionOffer.package.iconWide"
						:providerShortName="promotionOffer.package.shortName"
						:lazy="false"
					/>
				</div>

				<PromotionRowOfferPresentation
					class="grid-item-b"
					:title="title"
					:promotionOffer="promotionOffer"
					:catalogueItem="catalogueItem"
					:inPromotionCatalogue="inPromotionCatalogue"
					:packagePlan="packagePlan"
					:isHighlighted="isHighlighted"
					:filterOption="filterOption"
				/>

				<PromotionRowOfferLabel
					:title="title"
					class="grid-item-c"
					:promotionOffer="promotionOffer"
					:packagePlan="packagePlan"
					:isHighlighted="isHighlighted"
					:localisedCountry="hardcodedCountry"
				/>

				<PromotionRowCTA
					:isHighlighted="isHighlighted"
					:hasPromotionTag="hasPromotedTag && !largeFreeTrial"
					class="grid-item-d"
				/>

				<div v-if="largeFreeTrial" class="grid-item-e promotion-row_details">
					<div class="promotion-row_details-item" v-for="item in freeTrialDetails" :key="item">
						<div class="icon-container">
							<FontAwesomeIcon class="check-icon" :icon="faCheck" />
						</div>
						<p class="item-text">{{ item }}</p>
					</div>
				</div>
			</div>
		</component>
	</div>
</template>

<script setup lang="ts">
import { type Offer } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { UsePromotion } from '@/helpers/composables/usePromotion';

import PromotionRowCTA from '@/features/buybox/components/list/PromotionRowCTA.vue';
import PromotionRowOfferLabel from '@/features/buybox/components/list/PromotionRowOfferLabel.vue';
import PromotionRowOfferPresentation from '@/features/buybox/components/list/PromotionRowOfferPresentation.vue';
import {
	GetPackageProviderPlansDocument,
	GetPackageProviderPlansQuery,
} from '@/features/buybox/graphql/queries/GetPackageProviderPlans.query';
import { useGeoSwiper } from '@/features/buybox/composables/useGeoSwiper';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { OFFER_ENTITYID_KEY, getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { OfferLocation } from '@/helpers/offer-helper';
import { FALLBACK_PROMOTION_OFFER, primeImpactCountries } from '@/helpers/promotion-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import JwTVModal from '@/ui-components/video-player-modals/JwTVModal.vue';
import { computed, defineAsyncComponent, inject, ref } from 'vue';
import { useExperiment } from '@/libraries/experiment';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { useDoubleStackExp } from '@/components/experiments/DoubleStackExp';
import { useAdServer } from '@/features/ad-server/composables/useAdServer';
import ListPromotionRowMBRExp from '@/components/experiments/ExpBuyRent/components/ListPromotionRowMBRExp.vue';
import { useBuyRentExp } from '@/components/experiments/ExpBuyRent';
import { useSofterFreeTrialV2Exp } from '@/components/experiments/SoftFreeTrialListV2';
import useUnderPerformingUrlsExp from '@/components/experiments/UnderPerformingUrlsExp';
import { useAppleBingeExp } from '@/components/experiments/AppleBingeExp';
import { usePrimePlayPromoExp } from '@/components/experiments/PrimePlayPromoExp';

const PromotionRowUISoftExp = defineAsyncComponent(
	() => import('@/components/experiments/SoftFreeTrialListV2/components/PromotionRowUISoftExp.vue')
);

export type PlanOffer = ReturnType<typeof getPlanOffer>;

// EXP MERGE BUY RENT
const { activeVariantWithControlGroup: expMergeBuyRentVariant } = useExperiment('EXP_MERGE_BUY_RENT');
// EXP MERGE BUY RENT

const props = withDefaults(
	defineProps<{
		title: TitleDetail;
		offers?: Offer[];
		additionalContexts?: SnowplowContext[];
		hardcodedFallbackOfferId?: number;
		/** prop passed to override the webLocale country,to fetch and display localised promotion offer */
		hardcodedCountry?: string | null;
		hardcodedShortName?: string;
		hideRemoveAds?: boolean;
		swiperIndex?: number;
		largeFreeTrial?: boolean;
	}>(),
	{
		offers: () => [],
		additionalContexts: () => [],
	}
);

const emit = defineEmits<{
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: UsePromotion['promotionOffer'];
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
	(e: 'impressionCallback'): void;
}>();

const container = ref<HTMLElement | null>(null);
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const buyboxLocation = inject('offerLocation', OfferLocation.REGULAR_BUYBOX);
const noOffer = inject<boolean | undefined>('noOffer', undefined);
const { placement } = getClickoutTrackingPlacement();

const { country, language } = storeToRefs(useLanguageStore());
const { xServeTrack } = storeToRefs(useAppStore());
const { adServerIds } = useAdServer(() => props.title.id);

const { setNoOfferTag } = useInternationalOffersStatus({ title: () => props.title });

const fetchGeoSwiperOffers = computed(() => !!props.hardcodedCountry && !!props.hardcodedShortName);

const { geoSwiperOffers } = useGeoSwiper({
	title: () => props.title,
	enable: fetchGeoSwiperOffers,
	offerShortName: () => props.hardcodedShortName,
	offerCountry: () => props.hardcodedCountry,
});

const offersForPromotion = computed(() => {
	if (fetchGeoSwiperOffers.value) {
		return geoSwiperOffers.value;
	}

	return props.offers;
});

const countryOrGeoCountry = computed(() => {
	return props.hardcodedCountry ?? country.value;
});

const { inPromotionCatalogue, promotionOffer, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: offersForPromotion,
	hardcodedShortName: () => props.hardcodedShortName,
	countryOrGeoCountry,
});

const { setDoubleStackTags } = useDoubleStackExp();
const { setBuyRentExpTags } = useBuyRentExp({ title: () => props.title });

const providerShortNames = computed(() =>
	fetchGeoSwiperOffers.value ? [props.hardcodedShortName] : [catalogueItem.value?.shortName ?? '']
);

const enableGetPackageProviderPlans = computed(
	() =>
		(catalogueItem.value?.placement && !!catalogueItem.value) ||
		(!catalogueItem.value?.placement && !inPromotionCatalogue.value) ||
		!!fetchGeoSwiperOffers.value
);

// includeAddons is required to fetch packages of channels. It doesn't appear to have any impact on non-channel packages, but calling directly for now until this is confirmed.
const isAmazonChannel = computed(() => catalogueItem.value?.shortName === 'aep');

const { result: packageProviderPlans } = useQuery<GetPackageProviderPlansQuery>(
	GetPackageProviderPlansDocument,
	() => ({
		country: countryOrGeoCountry.value,
		language: language.value,
		packages: providerShortNames.value,
		includeAddons: isAmazonChannel.value,
	}),
	{
		enabled: enableGetPackageProviderPlans,
		errorPolicy: 'all',
		prefetch: false,
	}
);

const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);
const { trackTagEvent, withArbitrageTracking, isArbitrating } = useArbitrage({ entityId: jwRawEntityId });

const crunchyRollUrl = computed(() => {
	const url = props.offers.find(offer => offer.package.packageId === 283)?.standardWebURL;
	if (!url) return 'http://crunchyroll.pxf.io/xk92Nv';

	return url.includes('crunchyroll.pxf.io') ? url : `http://crunchyroll.pxf.io/xk92Nv?u=${url}`;
});

const packagePlan = computed(() => {
	const mainPackage = inPromotionCatalogue.value
		? promotionOffer.value?.package
		: packageProviderPlans.value?.packages?.[0];

	if (!mainPackage) return null;

	return getPlanOffer(mainPackage);
});

const rowNumber = computed(() => {
	if (container.value == null) return undefined;

	const parentBuyBox = container.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(container.value));

	return index === -1 ? undefined : index;
});

// OVER_PROVIDER
// Bit of an odd case for the Promotion Placement to be a regular offer - I believe BI would like this for JWTV as well, so I think we will develop in this direction
// In this case over provider is halfway between a regular offer and a promotion offer, but they want to start tracking it as a regular offer
const { isOverProviderTitle } = useOverProviderExp({ promotionOffer });
const clickoutType = computed(() => (isOverProviderTitle.value ? 'regular' : 'free_trial'));
const offerLocation = computed(() => (isOverProviderTitle.value ? buyboxLocation : OfferLocation.FREE_TRIAL));
// OVER_PROVIDER

// Apple Binge Exp
const { setAppleBingeExpParams } = useAppleBingeExp();

/** Applies `uct_country`, `rowNumber`, `offerLocation`. */
function clickoutBase(u: ClickoutUrl) {
	return u
		.set('uct_country', country.value)
		.addContexts(props.additionalContexts)
		.addContexts(titleContext.value)
		.setFreeTrial(!isOverProviderTitle.value)
		.setTitleSourceId(titleSourceId)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.setForTracking('offerLocation', offerLocation.value)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType.value)
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.updateContext('ClickoutContextGraphql', 'swiperIndex', props.swiperIndex)
		.setPageType(props.title.objectType)
		.setCanary(xServeTrack.value)
		.setAdIds(adServerIds.value)
		.setGenre(props.title.content.genres);
}

const hasAppleTvPlusOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 350));
const hasCrunchyRollOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 283));
const hasHuluOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 15));

const { setSofterFreeTrialV2Tags } = useSofterFreeTrialV2Exp();

const { setUnderPerformingUrlsExpTags } = useUnderPerformingUrlsExp();

const { setPrimePlayPromoTags } = usePrimePlayPromoExp();

const clickoutLink = computed(() => {
	if (!promotionOffer.value) return null;

	const packageId = promotionOffer.value?.package.packageId ?? 350;

	// // temporary bypass of uct for Crunchyroll
	if (packageId === 283) {
		return new ClickoutUrl({ ...promotionOffer.value, standardWebURL: crunchyRollUrl.value })
			.apply(clickoutBase)
			.apply(url => setDoubleStackTags(url, 'ft', promotionOffer.value?.package.shortName))
			.apply(url =>
				setBuyRentExpTags({
					url: url,
					type: 'ft',
					shortName: promotionOffer.value!.package.shortName,
					monetizationType: promotionOffer.value!.monetizationType,
				})
			)
			.apply(url =>
				setSofterFreeTrialV2Tags(
					url,
					promotionOffer.value?.package.shortName,
					'ft',
					props.title.objectType,
					promotionOffer.value?.package.technicalName
				)
			)
			.apply(url =>
				setPrimePlayPromoTags(
					url,
					'ft',
					promotionOffer.value?.package.shortName,
					promotionOffer.value?.package.technicalName
				)
			);
		// EXP MERGE BUY RENT
	}

	if (isArbitrating.value && ['amp', 'atp'].includes(promotionOffer.value.package.shortName)) {
		const arbitrageUrl = withArbitrageTracking({
			offer: promotionOffer,
			title: () => props.title,
			placement: 'ft',
		});
		if (arbitrageUrl) {
			return arbitrageUrl.apply(clickoutBase).setGenre(props.title?.content.genres);
		}
	}

	const clickoutUrl = new ClickoutUrl(promotionOffer.value).apply(clickoutBase);

	// GEO_SWIPER
	if (props.hardcodedCountry) {
		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			const tag = props.hardcodedCountry === 'GB' ? 'amz-gb-ft-swiper-21' : 'amz-us-ft-swiper-20';

			const clickoutUrl = new ClickoutUrl(promotionOffer.value)
				.apply(clickoutBase)
				.set('uct_ctag', tag)
				.set('uct_country', props.hardcodedCountry);

			if (!inPromotionCatalogue.value) {
				clickoutUrl.set('uct_ct', 'fallback');
			}

			return clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
		} else {
			const tag = `${
				promotionOffer.value.package.shortName
			}-${props.hardcodedCountry.toLowerCase()}-ft-swiper-${country.value.toLowerCase()}`;
			clickoutUrl.set('uct_ctag', tag);
		}
	}
	// GEO_SWIPER

	if (promotionOffer.value?.package.packageId !== 350 && promotionOffer.value?.id === FALLBACK_PROMOTION_OFFER) {
		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			const offer = promotionOffer.value;
			const amazonPrimeUrl = new ClickoutUrl(offer).apply(clickoutBase).set('uct_ct', 'fallback');
			if (!primeImpactCountries.includes(country.value) && country.value !== 'GB') {
				amazonPrimeUrl.skipYieldOptimization();
			}

			// temporarily added: add a tag on no offer pages in certain countries, so we can compare them against international offer noOffer pages
			amazonPrimeUrl.apply(setNoOfferTag);

			// EXP MERGE BUY RENT
			amazonPrimeUrl.apply(url =>
				setBuyRentExpTags({
					url: url,
					type: 'ft',
					shortName: promotionOffer.value!.package.shortName,
					monetizationType: promotionOffer.value!.monetizationType,
				})
			);
			// EXP MERGE BUY RENT
			amazonPrimeUrl.apply(u => setUnderPerformingUrlsExpTags(u, 'ft', promotionOffer.value!.package.shortName));
			amazonPrimeUrl.apply(url =>
				setSofterFreeTrialV2Tags(
					url,
					promotionOffer.value?.package.shortName,
					'ft',
					props.title.objectType,
					promotionOffer.value?.package.technicalName
				)
			);
			amazonPrimeUrl.apply(url =>
				setPrimePlayPromoTags(
					url,
					'ft',
					promotionOffer.value?.package.shortName,
					promotionOffer.value?.package.technicalName
				)
			);

			return amazonPrimeUrl.set(
				'r',
				amazonPrimeUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com')
			);
		}

		clickoutUrl.apply(url => setDoubleStackTags(url, 'ft', promotionOffer.value?.package.shortName));

		// EXP MERGE BUY RENT
		clickoutUrl.apply(url =>
			setBuyRentExpTags({
				url: url,
				type: 'ft',
				shortName: promotionOffer.value!.package.shortName,
				monetizationType: promotionOffer.value!.monetizationType,
			})
		);
		// EXP MERGE BUY RENT
		clickoutUrl.apply(u => setUnderPerformingUrlsExpTags(u, 'ft', promotionOffer.value!.package.shortName));
		clickoutUrl.apply(u => setAppleBingeExpParams(u, promotionOffer.value!.package.shortName));
		clickoutUrl.apply(url =>
			setSofterFreeTrialV2Tags(
				url,
				promotionOffer.value?.package.shortName,
				'ft',
				props.title.objectType,
				promotionOffer.value?.package.technicalName
			)
		);
		clickoutUrl.apply(url =>
			setPrimePlayPromoTags(
				url,
				'ft',
				promotionOffer.value?.package.shortName,
				promotionOffer.value?.package.technicalName
			)
		);

		return clickoutUrl;
	}

	const conversionTrackingByPackageId: Record<number, Record<'param' | 'value', string | null>> = {
		350: { value: hasAppleTvPlusOffer.value ? 'atds' : 'natds', param: 'ct' },
		283: { value: hasCrunchyRollOffer.value ? 'free_trial' : null, param: 'subId3' },
		15: { value: hasHuluOffer.value ? 'free_trial' : null, param: 'sub1' },
	};

	const partnerTokenByPackageId: Record<number, Record<'param' | 'value', string>> = {
		350: {
			value: 'justwatch_tv',
			param: 'itsct',
		},
	};

	if (partnerTokenByPackageId[packageId]?.value) {
		clickoutUrl.setOnOffer(partnerTokenByPackageId[packageId].param, partnerTokenByPackageId[packageId].value);
	}

	if (!!conversionTrackingByPackageId[packageId] && conversionTrackingByPackageId[packageId].param) {
		clickoutUrl.setOnOffer(
			conversionTrackingByPackageId[packageId].param!,
			conversionTrackingByPackageId[packageId].value
		);
	}

	if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
		clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	clickoutUrl.apply(url => setDoubleStackTags(url, 'ft', promotionOffer.value?.package.shortName));

	clickoutUrl.apply(u => setUnderPerformingUrlsExpTags(u, 'ft', promotionOffer.value!.package.shortName));
	clickoutUrl.apply(url =>
		setBuyRentExpTags({
			url: url,
			type: 'ft',
			shortName: promotionOffer.value!.package.shortName,
			monetizationType: promotionOffer.value!.monetizationType,
		})
	);

	clickoutUrl.apply(u => setAppleBingeExpParams(u, promotionOffer.value!.package.shortName));

	clickoutUrl.apply(url =>
		setSofterFreeTrialV2Tags(
			url,
			promotionOffer.value?.package.shortName,
			'ft',
			props.title.objectType,
			promotionOffer.value?.package.technicalName
		)
	);

	clickoutUrl.apply(url =>
		setPrimePlayPromoTags(
			url,
			'ft',
			promotionOffer.value?.package.shortName,
			promotionOffer.value?.package.technicalName
		)
	);

	return clickoutUrl;
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
	groupId: 'listPromotionRow',
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible || !clickoutLink.value) return;

	onIntersection(clickoutLink.value, isVisible);
}

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const hasPromotedTag = computed(() => !inPromotionCatalogue.value);
// CTA has a border, and different text. Fallback only.
const isHighlighted = computed(
	() => !inPromotionCatalogue.value && (props.title.__typename === 'Show' || props.title.__typename === 'Season')
);
const isModalProvider = computed(() => promotionOffer.value?.package.shortName === 'jwt');

const { t } = useI18n();
const freeTrialDetails = [
	t('WEBAPP_PP_FT_EXP_DETAILS_1'),
	t('WEBAPP_PP_FT_EXP_DETAILS_2'),
	t('WEBAPP_PP_FT_EXP_DETAILS_3'),
];

function openModal() {
	if (!isModalProvider.value) return;
	if (clickoutLink.value) trackClickout(clickoutLink.value);

	ModalHelper.openModal(
		JwTVModal,
		{
			offer: promotionOffer.value,
			title: props.title,
			additionalContexts: getClickoutUrlContextsArray(clickoutLink.value),
		},
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	onClickOut();
}

function getPlanOffer(pckg: TitleOfferFragment['package'] | GetPackageProviderPlansQuery['packages'][0]) {
	if (pckg.planOffers.length > 0) {
		const plan = pckg.planOffers.find(offer => offer.isTrial || offer.children.length > 0);
		if (plan && plan.children.length > 0) {
			return { ...plan.children[0], retailPrice: plan.retailPrice };
		}
		return plan;
	}
	return null;
}

function onClickOut() {
	trackTagEvent(promotionOffer);

	emit('clickOut', {
		buyboxOffer: promotionOffer?.value,
		offerLocation: OfferLocation.FREE_TRIAL,
		row: container.value,
	});
}

// EXP_SOFTER_FREE_TRIAL_V2
const { activeVariant } = useExperiment('EXP_SOFTER_FREE_TRIAL_V2');
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.offer {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 48px;
	width: 100%;
	min-height: 104px;
	padding: 16px;
	background-color: inherit;

	&.p-0 {
		padding: 0px;
	}

	&.is-large-freetrial {
		position: relative;
		min-height: 208px;

		@media (max-width: 700px) {
			height: 302px;
			min-height: 302px;
		}

		.grid-container {
			position: relative;
			z-index: 1;
		}
	}

	/* Assign grid areas for web layout */
	.grid-item-a {
		grid-area: icon;
	}

	.grid-item-b {
		/* Gap only between B and C on large view */
		padding-right: 16px;
		@media (max-width: 1024px) {
			padding-right: 0;
		}
		grid-area: presentation;
	}

	.grid-item-c {
		grid-area: label;
	}

	.grid-item-d {
		grid-area: cta;
		justify-self: stretch;
	}

	.grid-item-e {
		grid-area: promoRemoveAds;
		align-self: stretch;
	}

	.grid-container {
		height: auto;
		display: grid;
		grid-template-columns: auto calc(50% - 158px) 1fr 1fr; /* Make [A B] and [C D] 50% width each */
		grid-auto-rows: auto; /* Flexible row heights */
		width: 100%;
		grid-template-areas: 'icon presentation label cta'; /* 1 x 4 grid */

		@media (max-width: 1345px) {
			grid-template-columns: auto minmax(calc(50% - 118px), 1fr) minmax(0, 1fr) auto;
		}

		@media (max-width: 1024px) {
			height: auto;
			grid-auto-rows: auto;

			grid-template-areas:
				'icon label' /* 2 x 2 grid */
				'presentation cta';
			grid-template-columns: minmax(0, 1fr) auto; /* left side can shrink to fit right side */
		}

		&-three-rows {
			height: 100%;
			grid-auto-rows: auto;
			grid-template-areas:
				'icon presentation label cta' /* 2 x 4 grid */
				'promoRemoveAds promoRemoveAds promoRemoveAds promoRemoveAds';

			@media (max-width: 1024px) {
				grid-template-areas:
					'icon label' /* 3 x 2 grid */
					'presentation cta'
					'promoRemoveAds promoRemoveAds';
			}
		}

		@media (max-width: 1024px) {
			gap: 8px;
		}
	}

	.picture-wrapper {
		min-width: 120px;
		min-height: 40px;
		margin-bottom: auto;
		margin-inline-end: 48px;

		@media (max-width: 1345px) {
			min-width: 80px;
		}

		@media (max-width: 1024px) {
			margin-inline-end: 0px;
			margin-bottom: 10px;
		}
	}
}

.has-border {
	border: 1px solid #fbc500;
	border-radius: 8px;
}

.offer-wrapper {
	position: relative;
}

.promoted-container {
	display: flex;
	color: #4c5a67;
	justify-content: space-between;
	margin-top: 4px;

	@media (max-width: 1024px) {
		margin-top: 0;
	}
}

.promotion-row_details {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	height: 100%;
	gap: 8px;
	margin-top: 16px;
	width: 100%;

	@media (max-width: 1024px) {
		margin-top: 4px;
	}

	&-item {
		display: flex;
		align-items: center;
		gap: 8px;
		justify-self: flex-start;
		width: 100%;
	}

	.icon-container {
		width: 32px;
		height: 32px;
		border-radius: 50%;
		background-color: #ffffff0a;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.check-icon {
		color: #d2d3d5;
		width: 16px;
		height: 16px;
	}

	.item-text {
		margin: 0;
		font-weight: 700;
		font-style: Bold;
		font-size: 14px;
		line-height: 24px;
		letter-spacing: 1.5%;
		color: #909398;
	}
}
</style>
